import React, { useState, useEffect } from 'react';
import { Clock, Save, Calendar, Link as LinkIcon, X } from 'lucide-react';
import { User } from '../../types';
import { todayLocal } from '../../utils/localDate';
import { toast } from '../../utils/toast';

interface TimesheetEntryFormProps {
  currentUser: User;
  onSuccess?: () => void;
  existingEntry?: any;
  /** Pre-fills the date field when creating a new entry (e.g. clicking an
   * empty day in the weekly grid). Ignored when editing an existing entry. */
  initialDate?: string;
}

const TimesheetEntryForm: React.FC<TimesheetEntryFormProps> = ({
  currentUser,
  onSuccess,
  existingEntry,
  initialDate
}) => {
  const [formData, setFormData] = useState({
    date: initialDate || todayLocal(),
    hoursWorked: '7.5',
    workDescription: '',
    projects: [] as string[],
    taskLinks: [] as any[]
  });
  
  const [projectInput, setProjectInput] = useState('');
  const [taskLinkInput, setTaskLinkInput] = useState({ title: '', url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [reportingTo, setReportingTo] = useState<any>(null);
  const [managers, setManagers] = useState<any[]>([]);

  // Load managers (TLs/PMs/Admins) on mount. Role eligibility is driven
  // purely by Keycloak role, not by matching someone's name/email — a
  // per-person name check here would silently break the moment that person
  // changes teams or leaves, and doesn't scale past whoever it was written for.
  useEffect(() => {
    fetch('/api/users')
      .then(res => (res.ok ? res.json() : []))
      .then(data => {
        const users: any[] = Array.isArray(data) ? data : [];
        const eligibleRoles = currentUser.role === 'devops'
          ? ['tl', 'admin', 'devops'] // devops reports to a TL/admin, or a peer devops lead
          : ['tl', 'pm', 'admin'];

        // Filter on the role arrays — `role` is only the user's primary role
        const managerList = users.filter((u: any) =>
          ((u.realmRoles || u.roles || [u.role]) as string[]).some(r => eligibleRoles.includes(r)) &&
          u.id !== currentUser.id
        );

        setManagers(managerList);

        // Auto-select first eligible manager if none selected yet
        setReportingTo((current: any) => {
          if (current || managerList.length === 0) return current;
          return {
            id: managerList[0].id || managerList[0]._id,
            name: managerList[0].name || managerList[0].username,
            role: managerList[0].role
          };
        });
      })
      .catch(err => console.error('Error loading managers:', err));
    // Intentionally excludes `reportingTo` — including it re-fetches /api/users
    // every time the user picks a different manager from the dropdown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.id, currentUser.role]);

  // Load existing entry if editing
  useEffect(() => {
    if (existingEntry) {
      setFormData({
        date: existingEntry.date,
        hoursWorked: existingEntry.hoursWorked.toString(),
        workDescription: existingEntry.workDescription,
        projects: existingEntry.projects || [],
        taskLinks: existingEntry.taskLinks || []
      });
      if (existingEntry.reportingTo) {
        setReportingTo(existingEntry.reportingTo);
      }
    }
  }, [existingEntry]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const hours = parseFloat(formData.hoursWorked);
      if (hours <= 0 || hours > 24) {
        throw new Error('Hours worked must be between 0 and 24');
      }

      if (!formData.workDescription.trim()) {
        throw new Error('Work description is required');
      }

      if (!reportingTo) {
        throw new Error('Please select a reporting manager');
      }

      const payload = {
        userId: currentUser.id,
        userName: currentUser.name,
        userEmail: currentUser.email,
        userRole: currentUser.role,
        date: formData.date,
        hoursWorked: hours,
        workDescription: formData.workDescription.trim(),
        projects: formData.projects,
        taskLinks: formData.taskLinks,
        reportingTo
      };

      const url = existingEntry 
        ? `/api/timesheets/${existingEntry._id}`
        : '/api/timesheets';
      
      const method = existingEntry ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit timesheet');
      }

      toast(`✅ Timesheet ${existingEntry ? 'updated' : 'submitted'} successfully!`);
      
      // Reset form
      setFormData({
        date: todayLocal(),
        hoursWorked: '7.5',
        workDescription: '',
        projects: [],
        taskLinks: []
      });

      if (onSuccess) onSuccess();

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit timesheet';
      setError(message);
      toast(`❌ ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const addProject = () => {
    if (projectInput.trim() && !formData.projects.includes(projectInput.trim())) {
      setFormData({
        ...formData,
        projects: [...formData.projects, projectInput.trim()]
      });
      setProjectInput('');
    }
  };

  const removeProject = (project: string) => {
    setFormData({
      ...formData,
      projects: formData.projects.filter(p => p !== project)
    });
  };

  const addTaskLink = () => {
    if (taskLinkInput.title.trim() && taskLinkInput.url.trim()) {
      setFormData({
        ...formData,
        taskLinks: [...formData.taskLinks, {
          type: 'other',
          title: taskLinkInput.title.trim(),
          url: taskLinkInput.url.trim()
        }]
      });
      setTaskLinkInput({ title: '', url: '' });
    }
  };

  const removeTaskLink = (index: number) => {
    setFormData({
      ...formData,
      taskLinks: formData.taskLinks.filter((_, i) => i !== index)
    });
  };

  const hoursFloat = parseFloat(formData.hoursWorked) || 0;
  const meetsTarget = hoursFloat >= 7.5;

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="p-2 rounded-lg bg-white/15">
          <Clock className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold">
            {existingEntry ? 'Edit' : 'Submit'} Daily Timesheet
          </h2>
          <p className="text-blue-100 text-sm">Log your work for the day and route it to your manager</p>
        </div>
      </div>

      <div className="p-6">
      {error && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date and Hours */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Date *
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              max={todayLocal()}
              required
              disabled={!!existingEntry}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Hours Worked * (Target: 7.5h)
            </label>
            <input
              type="number"
              step="0.5"
              min="0"
              max="24"
              value={formData.hoursWorked}
              onChange={(e) => setFormData({ ...formData, hoursWorked: e.target.value })}
              required
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                meetsTarget ? 'border-emerald-500' : 'border-amber-500'
              }`}
            />
            <p className={`text-xs mt-1 ${meetsTarget ? 'text-emerald-600' : 'text-amber-600'}`}>
              {meetsTarget ? '✓ Meets 7.5h target' : '⚠ Below 7.5h target'}
            </p>
          </div>
        </div>

        {/* Reporting Manager */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reporting To (TL/PM) *
          </label>
          <select
            value={reportingTo?.id || ''}
            onChange={(e) => {
              const manager = managers.find(m => (m.id || m._id) === e.target.value);
              if (manager) {
                setReportingTo({
                  id: manager.id || manager._id,
                  name: manager.name || manager.username,
                  role: manager.role
                });
              }
            }}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select Manager</option>
            {managers.map(manager => (
              <option key={manager.id || manager._id} value={manager.id || manager._id}>
                {manager.name || manager.username} ({manager.role.toUpperCase()})
              </option>
            ))}
          </select>
        </div>

        {/* Work Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Work Description * (What did you work on today?)
          </label>
          <textarea
            value={formData.workDescription}
            onChange={(e) => setFormData({ ...formData, workDescription: e.target.value })}
            required
            rows={6}
            placeholder="Describe your work activities, tasks completed, issues resolved, etc..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.workDescription.length} characters
          </p>
        </div>

        {/* Projects/Modules */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Projects/Modules Worked On
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={projectInput}
              onChange={(e) => setProjectInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addProject())}
              placeholder="e.g., Authentication Module, API Integration"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={addProject}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.projects.map((project, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {project}
                <button
                  type="button"
                  onClick={() => removeProject(project)}
                  className="hover:text-blue-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Task Links (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <LinkIcon className="w-4 h-4 inline mr-1" />
            Related Task Links (Optional)
          </label>
          <div className="space-y-2 mb-2">
            <input
              type="text"
              value={taskLinkInput.title}
              onChange={(e) => setTaskLinkInput({ ...taskLinkInput, title: e.target.value })}
              placeholder="Task title"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="flex gap-2">
              <input
                type="url"
                value={taskLinkInput.url}
                onChange={(e) => setTaskLinkInput({ ...taskLinkInput, url: e.target.value })}
                placeholder="https://..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={addTaskLink}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Add Link
              </button>
            </div>
          </div>
          <div className="space-y-1">
            {formData.taskLinks.map((link, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    {link.title}
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => removeTaskLink(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <Save className="w-5 h-5" />
            {submitting ? 'Submitting...' : existingEntry ? 'Update Timesheet' : 'Submit Timesheet'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default TimesheetEntryForm;
